import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { rgba } from "polished";
import { theme } from "../theme";
import { Box, Flex } from "./layout";

const sizeMap = {
  sm: {
    checkbox: "1",
  },
  md: {
    checkbox: "1.25",
  },
  lg: {
    checkbox: "1.5",
  },
};

const SwitchWrapper = styled(Flex)(({
  error,
  color,
  size,
  disabled,
  fullWidth,
  lableBold,
  lableFirst,
  checked,
}) => {
  const bgColor = error
    ? theme.colors.danger.main
    : theme.colors[color]?.light || theme.colors.default.dark;

  const checkboxSize = sizeMap[size]?.checkbox || sizeMap.md.checkbox;

  return {
    position: "relative",
    alignItems: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    width: fullWidth ? "100%" : "auto",
    display: fullWidth ? "flex" : "inline-flex",

    "& label": {
      display: "flex",
      flexDirection: lableFirst ? "row-reverse" : "row",
      width: "100%",
      alignItems: "center",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      transition: "color 0.2s ease",
      position: "relative",
      gap: "0.5rem",
    },
    ".switchLabel": {
      color: disabled ? theme.colors.default.main : theme.colors.default.dark,
      fontWeight: lableBold ? 600 : 400,
    },
    ".hiddenCheckbox": {
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      zIndex: 2,
      opacity: 0,
      margin: 0,
      cursor: disabled ? "not-allowed" : "pointer",
    },

    ".customSwitch": {
      width: `${checkboxSize * 2}rem`,
      height: `${checkboxSize}rem`,
      borderRadius: `${checkboxSize / 2}rem`,
      padding: "0.125rem",
      backgroundColor: checked
        ? theme.colors[color]?.main || theme.colors.primary.main
        : rgba(bgColor, 0.5),
      transition: "all 0.2s ease",
      position: "relative",
      cursor: disabled ? "not-allowed" : "pointer",
      "&::after": {
        content: `''`,
        width: `${checkboxSize - 0.25}rem`,
        height: `${checkboxSize - 0.25}rem`,
        borderRadius: "50%",
        backgroundColor: theme.colors.light.light,
        position: "absolute",
        top: "50%",
        display: "flex",
        left: checked ? "auto" : "0.125rem",
        right: checked ? "0.125rem" : "auto",
        transform: "translateY(-50%)",
        transition: "all 0.2s ease",
      },
    },
    ".errorText": {
      fontSize: "0.75rem",
      color: error ? theme.colors.danger.main : "transparent",
      marginTop: "0.25rem",
    },
  };
});

export const Switchbox = ({
  label,
  color = "default",
  size = "md",
  fullWidth = false,
  lableFirst,
  lableBold,
  error,
  checked,
  disabled,
  onChange,
}) => {
  const [internalChecked, setInternalChecked] = useState(checked);

  useEffect(() => {
    setInternalChecked(checked);
  }, [checked]);

  const handleChange = () => {
    if (!disabled) {
      const newChecked = !internalChecked; // Toggle the checked state
      setInternalChecked(newChecked);
      if (onChange) {
        onChange(newChecked); // Call the passed onChange prop with the new state
      }
    }
  };

  return (
    <SwitchWrapper
      color={color}
      size={size}
      fullWidth={fullWidth}
      lableBold={lableBold}
      lableFirst={lableFirst}
      error={error}
      checked={internalChecked}
      disabled={disabled}
    >
      <label onClick={handleChange}>
        <input
          type="checkbox"
          className="hiddenCheckbox"
          checked={internalChecked}
          disabled={disabled}
          readOnly // Prevents manual changes; handled by label click
        />
        <Box className={`customSwitch`} />
        {label && <Box className="switchLabel">{label}</Box>}
      </label>
      {error && <span className="errorText">This field has an error.</span>}
    </SwitchWrapper>
  );
};

Switchbox.defaultProps = {
  label: "",
  color: "default",
  size: "md",
  fullWidth: false,
  lableFirst: false,
  lableBold: false,
  checked: false,
  error: false,
  disabled: false,
  onChange: () => {},
};

Switchbox.propTypes = {
  label: PropTypes.string,
  color: PropTypes.oneOf(["default", "primary", "secondary", "danger"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  fullWidth: PropTypes.bool,
  lableFirst: PropTypes.bool,
  lableBold: PropTypes.bool,
  checked: PropTypes.bool,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

export default Switchbox;
